import { useState, useEffect, useCallback } from 'react';

const API = 'https://alterus.onrender.com';

function Spinner() {
  return <span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid #1e2235', borderTop: '2px solid #6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite', verticalAlign: 'middle', marginRight: 6 }} />;
}

const PLATFORM_COLORS = {
  gmail:   { color: '#EA4335', label: 'Gmail',   icon: '📧' },
  outlook: { color: '#0078D4', label: 'Outlook', icon: '💼' },
  teams:   { color: '#6264A7', label: 'Teams',   icon: '💬' },
  other:   { color: '#5A607A', label: 'Other',   icon: '📩' },
};

// ── Email Card ─────────────────────────────────────────────────────────────────
function EmailCard({ email, onDraft, drafting, activeDraft, onSend, sending }) {
  const isActive = activeDraft?.id === email.id;
  const [showCompose, setShowCompose] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [to, setTo] = useState(email.from_email || '');
  const [subject, setSubject] = useState(`Re: ${email.subject || ''}`);

  useEffect(() => {
    if (isActive && activeDraft?.newDraft) {
      setDraftText(activeDraft.newDraft);
      setShowCompose(true);
    }
  }, [isActive, activeDraft]);

  return (
    <div style={{
      background: '#10131e',
      border: `1px solid ${isActive ? '#6366f1' : '#1e2235'}`,
      borderLeft: `3px solid ${email.unread ? '#6366f1' : '#1e2235'}`,
      borderRadius: 8, padding: 12, marginBottom: 8,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            {email.unread && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#6366f1', display: 'inline-block' }} />}
            <span style={{ fontSize: 12, fontWeight: 600, color: '#e8eaed' }}>{email.from}</span>
            <span style={{ fontSize: 10, color: '#3a3f55', fontFamily: 'DM Mono', marginLeft: 'auto' }}>{email.time}</span>
          </div>
          <div style={{ fontSize: 12, color: '#8b8fa8', marginBottom: 4 }}>{email.subject}</div>
          <div style={{ fontSize: 11, color: '#5a607a', lineHeight: 1.5 }}>{email.preview}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button
          onClick={() => onDraft(email)}
          disabled={drafting}
          style={{ padding: '5px 12px', background: '#6366f1', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}
        >
          {drafting && isActive ? <><Spinner />Drafting...</> : '✍️ Draft Reply'}
        </button>
      </div>

      {showCompose && (
        <div style={{ marginTop: 10 }}>
          <input value={to} onChange={e => setTo(e.target.value)} placeholder="To"
            style={{ width: '100%', background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 6, padding: '6px 10px', color: '#e8eaed', fontSize: 11, fontFamily: 'DM Mono', marginBottom: 6, boxSizing: 'border-box', outline: 'none' }} />
          <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject"
            style={{ width: '100%', background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 6, padding: '6px 10px', color: '#e8eaed', fontSize: 11, fontFamily: 'DM Mono', marginBottom: 6, boxSizing: 'border-box', outline: 'none' }} />
          <textarea value={draftText} onChange={e => setDraftText(e.target.value)}
            style={{ width: '100%', background: '#0a0d16', border: '1px solid #6366f1', borderRadius: 6, padding: '8px 10px', color: '#e8eaed', fontSize: 12, fontFamily: 'DM Mono', resize: 'vertical', minHeight: 100, outline: 'none', boxSizing: 'border-box', marginBottom: 6 }} />
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => onSend({ to, subject, body: draftText })} disabled={sending}
              style={{ padding: '5px 14px', background: '#22c55e', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>
              {sending ? <><Spinner />Sending...</> : '📤 Send'}
            </button>
            <button onClick={() => navigator.clipboard.writeText(draftText)}
              style={{ padding: '5px 10px', background: '#1e2235', color: '#8b8fa8', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>
              ⎘ Copy
            </button>
            <button onClick={() => setShowCompose(false)}
              style={{ padding: '5px 10px', background: '#1e2235', color: '#8b8fa8', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>
              ✕ Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Teams Message Card ─────────────────────────────────────────────────────────
function TeamsCard({ msg, onDraft, drafting, activeDraft }) {
  const isActive = activeDraft?.id === msg.id;
  const [showDraft, setShowDraft] = useState(false);
  const [draftText, setDraftText] = useState('');

  useEffect(() => {
    if (isActive && activeDraft?.newDraft) {
      setDraftText(activeDraft.newDraft);
      setShowDraft(true);
    }
  }, [isActive, activeDraft]);

  const formatTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const now = new Date();
      const diff = now - d;
      if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      if (diff < 172800000) return 'Yesterday';
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  return (
    <div style={{
      background: '#10131e',
      border: `1px solid ${isActive ? '#6264A7' : '#1e2235'}`,
      borderLeft: '3px solid #6264A7',
      borderRadius: 8, padding: 12, marginBottom: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#e8eaed' }}>{msg.from}</span>
        {msg.topic && (
          <span style={{ fontSize: 10, background: '#6264A722', color: '#6264A7', padding: '1px 6px', borderRadius: 4, fontFamily: 'DM Mono' }}>
            {msg.chatType === 'oneOnOne' ? '💬 DM' : `# ${msg.topic}`}
          </span>
        )}
        <span style={{ fontSize: 10, color: '#3a3f55', fontFamily: 'DM Mono', marginLeft: 'auto' }}>{formatTime(msg.time)}</span>
      </div>
      <div style={{ fontSize: 12, color: '#8b8fa8', lineHeight: 1.5, marginBottom: 8 }}>{msg.message}</div>

      <button onClick={() => onDraft(msg)} disabled={drafting}
        style={{ padding: '5px 12px', background: '#6264A7', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>
        {drafting && isActive ? <><Spinner />Drafting...</> : '✍️ Draft Reply'}
      </button>

      {showDraft && draftText && (
        <div style={{ marginTop: 10 }}>
          <textarea value={draftText} onChange={e => setDraftText(e.target.value)}
            style={{ width: '100%', background: '#0a0d16', border: '1px solid #6264A7', borderRadius: 6, padding: '8px 10px', color: '#e8eaed', fontSize: 12, fontFamily: 'DM Mono', resize: 'vertical', minHeight: 80, outline: 'none', boxSizing: 'border-box', marginBottom: 6 }} />
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => navigator.clipboard.writeText(draftText)}
              style={{ padding: '5px 14px', background: '#6264A7', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>
              ⎘ Copy to Teams
            </button>
            <button onClick={() => setShowDraft(false)}
              style={{ padding: '5px 10px', background: '#1e2235', color: '#8b8fa8', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>
              ✕ Close
            </button>
          </div>
          <div style={{ fontSize: 10, color: '#3a3f55', marginTop: 6, fontFamily: 'DM Mono' }}>
            Copy the draft and paste it into Teams
          </div>
        </div>
      )}
    </div>
  );
}

// ── Connect Banner ─────────────────────────────────────────────────────────────
function ConnectBanner({ platform, onConnect, connected, userEmail }) {
  const p = PLATFORM_COLORS[platform];
  if (connected) return null;
  return (
    <div style={{
      background: '#10131e', border: `1px solid ${p.color}33`,
      borderRadius: 8, padding: '14px 16px', marginBottom: 12,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#e8eaed', marginBottom: 3 }}>
          {p.icon} Connect {p.label}
        </div>
        <div style={{ fontSize: 11, color: '#5a607a' }}>
          Read your inbox and draft replies in your voice
        </div>
      </div>
      <button onClick={() => onConnect(platform)}
        style={{ padding: '7px 16px', background: p.color, color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
        Connect {p.label}
      </button>
    </div>
  );
}

// ── Empty State ────────────────────────────────────────────────────────────────
function EmptyState({ icon, lines }) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 0', color: '#3a3f55' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontFamily: 'DM Mono', fontSize: 12, lineHeight: 1.8 }}>
        {lines.map((l, i) => <span key={i}>{l}<br /></span>)}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function InboxTab({ name, email }) {
  const [activeTab, setActiveTab] = useState('outlook');
  const [tone, setTone] = useState('balanced');

  // Outlook state
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [outlookEmails, setOutlookEmails]       = useState([]);
  const [outlookLoading, setOutlookLoading]     = useState(false);

  // Teams state  ← NEW
  const [teamsMessages, setTeamsMessages] = useState([]);
  const [teamsLoading, setTeamsLoading]   = useState(false);
  const [teamsFetched, setTeamsFetched]   = useState(false);

  // Draft state
  const [drafting, setDrafting]       = useState(false);
  const [activeDraft, setActiveDraft] = useState(null);
  const [sending, setSending]         = useState(false);

  // Activity feed
  const [activity, setActivity]           = useState({ gmail: [], outlook: [], teams: [], other: [], total: 0 });
  const [activityLoading, setActivityLoading] = useState(false);

  // Check OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('outlook_connected')) {
      setOutlookConnected(true);
      window.history.replaceState({}, '', '/dashboard');
    }
  }, []);

  // Check Outlook status on mount
  const checkOutlookStatus = useCallback(async () => {
    if (!email) return;
    try {
      const res  = await fetch(`${API}/outlook/status?user_email=${encodeURIComponent(email)}`);
      const data = await res.json();
      setOutlookConnected(data.connected);
      if (data.connected) fetchOutlookInbox();
    } catch (e) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  useEffect(() => {
    if (email) {
      checkOutlookStatus();
      loadActivity();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // Fetch Teams messages when Teams tab is opened  ← NEW
  useEffect(() => {
    if (activeTab === 'teams' && outlookConnected && !teamsFetched) {
      fetchTeamsMessages();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, outlookConnected]);

  const fetchOutlookInbox = async () => {
    setOutlookLoading(true);
    try {
      const res  = await fetch(`${API}/outlook/inbox?user_email=${encodeURIComponent(email)}&top=20`);
      const data = await res.json();
      if (data.emails) setOutlookEmails(data.emails);
      if (data.error === 'not_connected') setOutlookConnected(false);
    } catch (e) {}
    setOutlookLoading(false);
  };

  // Fetch Teams messages from Graph API  ← NEW
  const fetchTeamsMessages = async () => {
    if (!email) return;
    setTeamsLoading(true);
    try {
      const res  = await fetch(`${API}/outlook/teams?user_email=${encodeURIComponent(email)}&limit=20`);
      const data = await res.json();
      if (data.messages) {
        setTeamsMessages(data.messages);
        setTeamsFetched(true);
      }
      if (data.error === 'not_connected') setOutlookConnected(false);
    } catch (e) {}
    setTeamsLoading(false);
  };

  const loadActivity = async () => {
    setActivityLoading(true);
    try {
      const res  = await fetch(`${API}/api/inbox/activity?user_email=${encodeURIComponent(email)}`);
      const data = await res.json();
      setActivity(data);
    } catch (e) {}
    setActivityLoading(false);
  };

  const handleConnect = (platform) => {
    // Outlook OAuth handles both email and Teams (same token, Chat.Read scope)
    if (platform === 'outlook' || platform === 'teams') {
      window.location.href = `${API}/outlook/login?user_email=${encodeURIComponent(email)}`;
    }
  };

  const handleDisconnect = async (platform) => {
    if (platform === 'outlook') {
      await fetch(`${API}/outlook/disconnect`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ user_email: email }),
      });
      setOutlookConnected(false);
      setOutlookEmails([]);
      setTeamsMessages([]);
      setTeamsFetched(false);
    }
  };

  const handleRefresh = () => {
    if (activeTab === 'outlook') fetchOutlookInbox();
    if (activeTab === 'teams')   { setTeamsFetched(false); fetchTeamsMessages(); }
    if (activeTab === 'activity') loadActivity();
  };

  // Draft an email reply
  const draftEmailReply = async (emailItem) => {
    setDrafting(true);
    setActiveDraft({ id: emailItem.id, newDraft: null });
    try {
      const res  = await fetch(`${API}/api/draft`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          platform:     'outlook',
          body:         emailItem.preview || emailItem.body || '',
          subject:      emailItem.subject || '',
          sender:       emailItem.from || '',
          sender_email: emailItem.from_email || '',
          user_name:    name  || '',
          user_email:   email || '',
          tone:         tone.toLowerCase(),   // FIX: was sending 'Balanced', backend expects lowercase
        }),
      });
      const data = await res.json();
      if (data.error && !data.draft) {
        alert(`Draft failed: ${data.error}`);
        setActiveDraft(null);
      } else {
        setActiveDraft({ id: emailItem.id, newDraft: data.draft || '' });
      }
    } catch (e) {
      alert('Draft failed — check your connection');
      setActiveDraft(null);
    }
    setDrafting(false);
  };

  // Draft a Teams reply  ← NEW
  const draftTeamsReply = async (msg) => {
    setDrafting(true);
    setActiveDraft({ id: msg.id, newDraft: null });
    try {
      const res  = await fetch(`${API}/api/draft`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          platform:  'teams',
          body:      msg.message || '',
          subject:   msg.topic   || '',
          sender:    msg.from    || '',
          user_name: name        || '',
          user_email: email      || '',
          tone:      tone.toLowerCase(),
        }),
      });
      const data = await res.json();
      if (data.error && !data.draft) {
        alert(`Draft failed: ${data.error}`);
        setActiveDraft(null);
      } else {
        setActiveDraft({ id: msg.id, newDraft: data.draft || '' });
      }
    } catch (e) {
      alert('Draft failed — check your connection');
      setActiveDraft(null);
    }
    setDrafting(false);
  };

  const sendEmail = async ({ to, subject, body }) => {
    setSending(true);
    try {
      const res  = await fetch(`${API}/outlook/send`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ user_email: email, to, subject, body }),
      });
      const data = await res.json();
      if (data.success) {
        setActiveDraft(null);
        alert('✅ Email sent!');
      } else {
        alert('❌ Send failed: ' + (data.error || 'Unknown error'));
      }
    } catch (e) {
      alert('❌ Send failed');
    }
    setSending(false);
  };

  // ── Tabs ───────────────────────────────────────────────────────────────────
  const tabs = [
    { id: 'outlook',  label: `💼 Outlook ${outlookConnected ? `(${outlookEmails.length})` : ''}` },
    { id: 'teams',    label: `💬 Teams ${teamsMessages.length > 0 ? `(${teamsMessages.length})` : ''}` },
    { id: 'gmail',    label: '📧 Gmail' },
    { id: 'activity', label: `📋 Activity (${activity.total})` },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 18 }}>📬</span>
        <span style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, color: '#e8eaed' }}>Inbox</span>
        <button onClick={handleRefresh}
          style={{ marginLeft: 'auto', background: '#1e2235', border: 'none', color: '#8b8fa8', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>
          🔄 Refresh
        </button>
      </div>

      {/* Tone selector */}
      <div style={{ background: '#10131e', border: '1px solid #1e2235', borderRadius: 8, padding: '8px 14px', marginBottom: 14, fontSize: 11, color: '#5a607a', fontFamily: 'DM Mono', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span>🎛️ Tone: <span style={{ color: '#6366f1', fontWeight: 600 }}>{tone.charAt(0).toUpperCase() + tone.slice(1)}</span></span>
        <div style={{ display: 'flex', gap: 6 }}>
          {['direct', 'balanced', 'diplomatic'].map(t => (
            <button key={t} onClick={() => setTone(t)}
              style={{ padding: '2px 10px', borderRadius: 6, border: `1px solid ${tone === t ? '#6366f1' : '#1e2235'}`, background: tone === t ? '#6366f122' : 'transparent', color: tone === t ? '#6366f1' : '#5a607a', cursor: 'pointer', fontSize: 11 }}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Platform tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${activeTab === t.id ? '#6366f1' : '#1e2235'}`, background: activeTab === t.id ? '#1e2235' : 'transparent', color: activeTab === t.id ? '#6366f1' : '#8b8fa8', cursor: 'pointer', fontSize: 12 }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Outlook Tab ─────────────────────────────────────────────────────── */}
      {activeTab === 'outlook' && (
        <div>
          {!outlookConnected ? (
            <>
              <ConnectBanner platform="outlook" onConnect={handleConnect} connected={false} userEmail={email} />
              <EmptyState icon="💼" lines={['Connect Outlook to see your inbox', 'and draft AI-powered replies in your voice.']} />
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontSize: 11, color: '#22c55e', fontFamily: 'DM Mono' }}>✅ Outlook connected · real-time via Graph API</span>
                <button onClick={() => handleDisconnect('outlook')}
                  style={{ fontSize: 10, color: '#5a607a', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                  Disconnect
                </button>
              </div>

              {outlookLoading ? (
                <div style={{ color: '#5a607a', fontSize: 13 }}><Spinner />Loading inbox...</div>
              ) : outlookEmails.length === 0 ? (
                <EmptyState icon="📭" lines={['No emails found in inbox.', 'New emails will appear here automatically.']} />
              ) : (
                outlookEmails.map((em, i) => (
                  <EmailCard
                    key={em.id || i}
                    email={em}
                    onDraft={draftEmailReply}
                    drafting={drafting}
                    activeDraft={activeDraft}
                    onSend={sendEmail}
                    sending={sending}
                  />
                ))
              )}
            </>
          )}
        </div>
      )}

      {/* ── Teams Tab ── NEW ─────────────────────────────────────────────────── */}
      {activeTab === 'teams' && (
        <div>
          {!outlookConnected ? (
            <>
              <ConnectBanner platform="teams" onConnect={handleConnect} connected={false} userEmail={email} />
              <EmptyState icon="💬" lines={['Connect Outlook to enable Teams.', 'Teams uses the same Microsoft login.', 'Messages load on-demand when you open this tab.']} />
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontSize: 11, color: '#6264A7', fontFamily: 'DM Mono' }}>
                  💬 Teams · on-demand polling
                </span>
                <button onClick={() => { setTeamsFetched(false); fetchTeamsMessages(); }}
                  style={{ fontSize: 10, color: '#5a607a', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                  Refresh
                </button>
              </div>

              {/* Note about Teams real-time */}
              <div style={{ background: '#10131e', border: '1px solid #6264A722', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 11, color: '#5a607a', fontFamily: 'DM Mono' }}>
                ℹ️ Teams messages are fetched on-demand. Real-time push requires IT admin approval for your org.
              </div>

              {teamsLoading ? (
                <div style={{ color: '#5a607a', fontSize: 13 }}><Spinner />Loading Teams messages...</div>
              ) : teamsMessages.length === 0 ? (
                <EmptyState icon="💬" lines={['No recent Teams messages found.', 'Make sure Teams is enabled in your Microsoft account.', 'Try refreshing — messages from recent chats will appear here.']} />
              ) : (
                teamsMessages.map((msg, i) => (
                  <TeamsCard
                    key={msg.id || i}
                    msg={msg}
                    onDraft={draftTeamsReply}
                    drafting={drafting}
                    activeDraft={activeDraft}
                  />
                ))
              )}
            </>
          )}
        </div>
      )}

      {/* ── Gmail Tab ── NEW ─────────────────────────────────────────────────── */}
      {activeTab === 'gmail' && (
        <div>
          <div style={{ background: '#10131e', border: '1px solid #EA433533', borderRadius: 8, padding: 20, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#e8eaed', marginBottom: 8 }}>📧 Gmail</div>
            <div style={{ fontSize: 12, color: '#8b8fa8', lineHeight: 1.7, marginBottom: 16 }}>
              Gmail integration works through the <strong style={{ color: '#EA4335' }}>Alterus Chrome Extension</strong>.
              Install the extension and it will detect Gmail threads in your browser and surface draft replies here in the Activity tab.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 6, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color: '#22c55e', fontWeight: 600, marginBottom: 4 }}>Step 1</div>
                <div style={{ fontSize: 12, color: '#8b8fa8' }}>Install the Alterus Chrome Extension from the Chrome Web Store</div>
              </div>
              <div style={{ background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 6, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color: '#22c55e', fontWeight: 600, marginBottom: 4 }}>Step 2</div>
                <div style={{ fontSize: 12, color: '#8b8fa8' }}>Open Gmail in your browser — the extension will add a "Draft with Alterus" button to each email</div>
              </div>
              <div style={{ background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 6, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color: '#22c55e', fontWeight: 600, marginBottom: 4 }}>Step 3</div>
                <div style={{ fontSize: 12, color: '#8b8fa8' }}>Drafts and activity appear in the Activity tab here</div>
              </div>
            </div>
          </div>

          {/* Show any Gmail activity from the extension */}
          {activity.gmail.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#e8eaed', marginBottom: 10 }}>
                Recent Gmail Activity ({activity.gmail.length})
              </div>
              {activity.gmail.map((entry, i) => (
                <div key={i} style={{ background: '#10131e', border: '1px solid #1e2235', borderLeft: '3px solid #EA4335', borderRadius: 8, padding: 12, marginBottom: 8 }}>
                  {entry.subject && <div style={{ fontSize: 12, fontWeight: 600, color: '#e8eaed', marginBottom: 4 }}>{entry.subject.slice(0, 60)}</div>}
                  {entry.draft_snippet && (
                    <div style={{ background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 6, padding: '6px 10px', fontSize: 11, color: '#8b8fa8', fontFamily: 'DM Mono', lineHeight: 1.5 }}>
                      {entry.draft_snippet}...
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Activity Tab ─────────────────────────────────────────────────────── */}
      {activeTab === 'activity' && (
        <div>
          {activityLoading ? (
            <div style={{ color: '#5a607a', fontSize: 13 }}><Spinner />Loading activity...</div>
          ) : activity.total === 0 ? (
            <EmptyState icon="📬" lines={[
              'No activity yet.',
              'Draft a reply from the Outlook or Teams tab,',
              'or use the Chrome extension in Gmail.',
              'All drafts and feedback appear here.',
            ]} />
          ) : (
            [...activity.gmail.map(e  => ({ ...e, platform: 'gmail'   })),
             ...activity.outlook.map(e => ({ ...e, platform: 'outlook' })),
             ...activity.teams.map(e  => ({ ...e, platform: 'teams'   })),
            ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
             .map((entry, i) => {
               const p = PLATFORM_COLORS[entry.platform] || PLATFORM_COLORS.other;
               return (
                 <div key={i} style={{ background: '#10131e', border: '1px solid #1e2235', borderLeft: `3px solid ${p.color}`, borderRadius: 8, padding: 12, marginBottom: 8 }}>
                   <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                     <span style={{ fontSize: 10, background: p.color + '22', color: p.color, padding: '1px 6px', borderRadius: 4, fontFamily: 'DM Mono' }}>{p.icon} {p.label}</span>
                     <span style={{ fontSize: 10, color: '#3a3f55', fontFamily: 'DM Mono', marginLeft: 'auto' }}>
                       {entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                     </span>
                   </div>
                   {entry.subject && <div style={{ fontSize: 12, fontWeight: 600, color: '#e8eaed', marginBottom: 3 }}>{entry.subject.slice(0, 60)}</div>}
                   {entry.draft_snippet && (
                     <div style={{ background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 6, padding: '6px 10px', fontSize: 11, color: '#8b8fa8', fontFamily: 'DM Mono', lineHeight: 1.5 }}>
                       {entry.draft_snippet}...
                     </div>
                   )}
                 </div>
               );
             })
          )}
        </div>
      )}
    </div>
  );
}
